import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createIndexedDbStorage } from '@/utils/storage/indexeddb-storage';
import { TTL } from '@/utils/storage/localStorage-with-ttl';

const initialValues = {
  cart: {},
  cartItems: [],
  refetchCart: undefined,
  _hasHydrated: false,
  relatedProducts: [],
  totalSegments: [],
  openDrawer: false,
};

const useCart = create(
  persist(
    (set) => ({
      ...initialValues,
      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
      setCart: (cart) => set({ cart: cart, cartItems: cart?.items }),

      addItem: (newItem) =>
        set((state) => {
          // Check if the item already exists in the cart
          const existingItem = state.cartItems.find(
            (item) => item.item_id === newItem.item_id
          );

          const updatedCartItems = existingItem
            ? state.cartItems.map((item) =>
                item.item_id === newItem.item_id
                  ? { ...item, qty: newItem.qty } // Update quantity if item exists
                  : item
              )
            : [...state.cartItems, newItem]; // Add new item if it doesn't exist
          return { ...state, cartItems: updatedCartItems };
        }),
      addItems: (newItems) =>
        set((state) => {
          let updatedCartItems = [...state.cartItems];

          newItems.forEach((newItem) => {
            const existingItem = updatedCartItems.find(
              (item) => item.item_id === newItem.item_id
            );

            if (existingItem) {
              // Update quantity if item exists
              updatedCartItems = updatedCartItems.map((item) =>
                item.item_id === newItem.item_id
                  ? { ...item, qty: newItem.qty }
                  : item
              );
            } else {
              // Add new item if it doesn't exist
              updatedCartItems.push(newItem);
            }
          });

          return { ...state, cartItems: updatedCartItems };
        }),
      updateItem: (itemId, newQty) =>
        set((state) => {
          // Update the quantity of the item in the cart
          const updatedCartItems = state.cartItems.map((item) => {
            if (item.item_id === itemId) {
              return { ...item, qty: newQty };
            }
            return item;
          });
          return { ...state, cartItems: updatedCartItems };
        }),
      removeItem: (itemId) =>
        set((state) => {
          // Remove the item from the cart
          const updatedCartItems = state.cartItems.filter(
            (item) => item.item_id !== itemId
          );
          return { ...state, cartItems: updatedCartItems };
        }),

      setRefetchCart: (refetchCart) => {
        set({ refetchCart });
      },

      resetCart: () => set(initialValues),

      setRelatedProducts: (relatedProducts) =>
        set((state) => {
          return { ...state, relatedProducts };
        }),

      setTotalSegments: (totalSegments) =>
        set((state) => {
          return { ...state, totalSegments };
        }),

      togglePopup: () =>
        set((state) => {
          return { ...state, cartpopup: !state.cartpopup };
        }),

      openCartPopup: () =>
        set((state) => {
          return { ...state, cartpopup: true };
        }),

      toggleDrawer: () =>
        set((state) => {
          return { ...state, openDrawer: !state?.openDrawer };
        }),

      closeAll: () =>
        set((state) => {
          return { ...state, cartpopup: false, openDrawer: false };
        }),
    }),
    {
      name: 'cart',
      onRehydrateStorage: (state) => {
        return () => state.setHasHydrated(true);
      },
      storage: createIndexedDbStorage('cart', TTL.ONE_DAY),
    }
  )
);

export default useCart;
